import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "@/App.css";
import { HERO_CLIPS, HERO_DURATION_MS, NAV_LINKS } from "@/config";

function App() {
  // One random clip per visit — the rotation
  const clip = useMemo(
    () => HERO_CLIPS[Math.floor(Math.random() * HERO_CLIPS.length)],
    []
  );
  const videoRef = useRef(null);
  const introStartRef = useRef(null);
  const videoEndedRef = useRef(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [soundOn, setSoundOn] = useState(false);
  const [fadingOut, setFadingOut] = useState(false);
  const [videoEnded, setVideoEnded] = useState(false);

  // Reveal the logo + menu when the intro finishes
  const handleVideoFinished = useCallback(() => {
    videoEndedRef.current = true;
    setVideoEnded(true);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // When the animated image layer starts (always), remember the moment —
    // used to sync the audio to wherever the intro currently is.
    const introStart = introStartRef.current || Date.now();

    // Try to start WITH sound first — allowed on many real browsers
    // (returning visitors, relaxed policies). If rejected, fall back to
    // muted autoplay; the first touch anywhere turns the music on.
    const onVolumeChange = () => setSoundOn(!video.muted);
    video.addEventListener("volumechange", onVolumeChange);
    (async () => {
      try {
        video.muted = false;
        video.volume = 1;
        await video.play();
      } catch (error) {
        video.muted = true;
        video.play().catch(() => {});
      }
    })();
    const onPlaying = () => {
      setVideoPlaying(true);
      // Backstop: if "ended" is ever missed, reveal just after the clip ends
      if (video.duration > 0) {
        window.setTimeout(
          handleVideoFinished,
          (video.duration - video.currentTime + 1.5) * 1000
        );
      }
    };
    video.addEventListener("playing", onPlaying);

    // Fade to black begins DURING the final image (the b/w child) — the
    // clip darkens gradually instead of stopping dead on a frozen frame.
    // Two triggers: timed to the animated image, and exact via playback.
    window.setTimeout(() => setFadingOut(true), HERO_DURATION_MS - 1400);

    // Gentle audio fade-out over the last 1.4s, and the exact visual fade
    const onTimeUpdate = () => {
      if (!video.duration) return;
      const left = video.duration - video.currentTime;
      if (!video.muted && left < 1.4) video.volume = Math.max(0, left / 1.4);
      if (left < 1.4) setFadingOut(true);
    };
    video.addEventListener("timeupdate", onTimeUpdate);

    // Music on first touch. If the video is playing, just unmute. If it was
    // blocked, join the intro at the CURRENT point of the animated image —
    // never restart from zero, so the visitor never sees the clip jump back.
    // Once the intro is over (logo + menu on screen), touches must NEVER
    // restart the video — it would play invisibly and the music would
    // ghost back over the menu. Guard on the ref: React state captured in
    // this one-time effect closure would stay stale forever.
    const enableSound = () => {
      if (videoEndedRef.current || video.ended) return;
      if (!video.paused && !video.muted) {
        window.removeEventListener("pointerdown", enableSound);
        window.removeEventListener("touchstart", enableSound);
        window.removeEventListener("keydown", enableSound);
        window.removeEventListener("click", enableSound);
        return;
      }
      if (video.paused && video.duration > 0) {
        const start = introStartRef.current || introStart;
        const elapsed = ((Date.now() - start) / 1000) % video.duration;
        video.currentTime = Math.min(elapsed, video.duration - 0.2);
      }
      video.muted = false;
      video.volume = 1;
      video.play().catch(() => {});
    };
    window.addEventListener("pointerdown", enableSound);
    window.addEventListener("touchstart", enableSound);
    window.addEventListener("keydown", enableSound);
    window.addEventListener("click", enableSound);

    // If the video never starts (blocked autoplay), the animated image is
    // moving underneath — time the reveal to the clip.
    window.setTimeout(() => {
      if (video.paused && video.currentTime === 0) handleVideoFinished();
    }, HERO_DURATION_MS + 400);

    // NOTE: deliberately no cleanup (single-mount page; React StrictMode's
    // dev double-effect would otherwise tear down the surviving bindings).
  }, [handleVideoFinished]);

  return (
    <div className="kende-stage" data-testid="kende-stage">
      <div
        className={fadingOut ? "kende-video kende-video--out" : "kende-video"}
        aria-hidden="true"
      >
        <img
          data-testid="hero-anim"
          className="kende-hero-media"
          src={clip.anim}
          alt=""
          draggable="false"
          onLoad={() => {
            if (!introStartRef.current) introStartRef.current = Date.now();
          }}
          onError={handleVideoFinished}
        />
        <video
          ref={videoRef}
          data-testid="hero-video"
          className={
            videoPlaying
              ? "kende-hero-media kende-hero-media--live"
              : "kende-hero-media kende-hero-media--idle"
          }
          src={clip.video}
          autoPlay
          playsInline
          preload="auto"
          disablePictureInPicture
          controlsList="nodownload nofullscreen noremoteplayback"
          onEnded={() => {
            setFadingOut(true);
            handleVideoFinished();
          }}
        />
      </div>

      {!videoEnded && !soundOn && (
        <div className="kende-sound-hint" data-testid="sound-hint" aria-hidden="true">
          tap for sound
        </div>
      )}

      <div
        className={videoEnded ? "kende-reveal kende-reveal--in" : "kende-reveal"}
        data-testid="reveal-screen"
        aria-hidden={!videoEnded}
      >
        <img
          className="kende-logo"
          data-testid="kende-logo"
          src="/kende-logo.png"
          alt="KENDE.DESIGN"
        />
        <div className="kende-logo-rule" aria-hidden="true" />
        <nav className="kende-nav" data-testid="nav-menu" aria-label="Main navigation">
          {NAV_LINKS.map((link, index) => (
            <a
              key={link.id}
              href={link.href}
              className="kende-nav-link"
              data-testid={`nav-link-${link.id}`}
              style={{ animationDelay: `${2 + index * 0.22}s` }}
            >
              {link.label}
            </a>
          ))}
        </nav>
      </div>
    </div>
  );
}

export default App;
